/** @jsxImportSource @emotion/react */
import React, { useEffect, useRef, useState } from 'react';
import _ from 'lodash';
import { Button } from '@mui/material';
import { CellType } from '@grapecity/wijmo.grid';
import { SubTitleLayout, SubTitleGroup, ControlBtnGroup } from 'components/layouts/ContentLayout';
import {
  SearchBox,
  SearchBoxRow,
  InputBox,
  SearchRows,
  SearchCols,
} from 'components/layouts/SearchBox';
import { IconButton } from 'components/buttons/IconSVG';
import {
  getGatingEquipmentMst,
  saveEquipments,
  getEquipments2,
  updateGatingDate,
} from 'apis/gtng/popup/GatingEquipment';
import { ContentFlex } from 'components/layouts/ContentFlex';
import { useTranslation } from 'react-i18next';
import CustomDialog from 'components/modals/common/CustomDialog';
import CustomGrid from 'components/grids/CustomGrid';
import CustomInputWithSearch from 'components/inputs/CustomInputWithSearch';
import SearchBoxButtons from 'components/buttons/SearchBoxButtons';
import { MpPlntList, PlntList, SearchParam } from 'models/mp/MpPlnt';
import { useMessageBar } from 'hooks/useMessageBar';
import UserMultiModal from 'components/modals/common/UserMultiModal';
import { getFactoryList } from 'apis/mp/MpFactory';
import useEvent from 'react-use-event-hook';
import { CrudCode } from 'models/common/Edit';
import { useCommonModal } from 'hooks/useCommonModal';

/**
 * @description Factory 선택 팝업
 * @author
 * -------------------------------------------------------------------------------------
 *  Date.       author         Description
 * -------------------------------------------------------------------------------------
 *  25.11.10    jihwan89       Factory 팝업에서 공장 체크 해제 후 선택 시 확인 알람 메세지 추가 (PI 요청)
 **/

interface Props {
  open: boolean;
  close: (isDone?: boolean) => void;
  condition: any;
  onCallbackData: any;
}

export const MpFactoryPopUp = ({ open, close, condition, onCallbackData }: Props) => {
  const { t } = useTranslation();
  const { openMessageBar } = useMessageBar();
  const { openCommonModal } = useCommonModal();

  const gridRef = useRef<any>();

  const [mpPlntRowData, setMpPlntRowData] = useState<MpPlntList[]>([]);
  const [conditionRowData, setConditionRowData] = useState<MpPlntList[]>([]);

  const initSearchParam = {
    copCd: '',
    prdnPlntCd: '',
    prdnPlntNm: '',
  };

  const [searchParam, setSearchParam] = useState<SearchParam>(initSearchParam);
  const [searchState, setSearchState] = useState<boolean>(false);

  const [mpPlntFlexRef, setMpPlntFlexRef] = useState<any>();
  const [mpPlntFlexItem, setMpPlntFlexItem] = useState<any>();

  const [userIds, setUserIds] = useState<string>('');
  const [rmkContext, setRmkContext] = useState<string>('');
  const [isRequiredRmk, setRequiredRmk] = useState<boolean>(false);

  const [isOpenUserMultiModal, setOpenUserMultiModal] = useState<boolean>(false);

  useEffect(() => {
    setConditionRowData(condition.rowData);
    setSearchParam({
      copCd: condition?.copCd || '',
    });
    setSearchState(true);
  }, [condition?.copCd]);

  useEffect(() => {
    // 공장추가(선택) 팝업 공장기본 조회
    if (searchState) {
      getFactorysList();
    }
  }, [searchState]);

  const getFactorysList = async () => {
    await getFactoryList(searchParam).then((result) => {
      if (result !== null) {
        const rowData = _.uniqBy(conditionRowData.concat(result), 'prdnPlntCd');
        setMpPlntRowData(rowData);
        setTimeout(() => {
          getSelectedRow(conditionRowData);
        }, 300);
      } else {
        openMessageBar({
          type: 'error',
          content: t(
            'com.msg.요청 정보 조회 중 오류가 발생했습니다.',
            '요청 정보 조회 중 오류가 발생했습니다.'
          ),
        });
        return false;
      }
    });
  };

  const mpPlntLayoutDefinition = [
    {
      binding: 'prdnPlntCd',
      header: String(t('mp.label.Factory ID', 'Factory ID')),
      align: 'left',
      isReadOnly: true,
      width: 150,
    },
    {
      binding: 'prdnPlntNm',
      header: String(t('mp.label.Factory 명', 'Factory 명')),
      align: 'left',
      isReadOnly: true,
      width: 200,
    },
    {
      binding: 'horExplCrgeUserId',
      header: String(t('mp.label.수평전개 담당자', '수평전개 담당자')),
      align: 'left',
      width: '*',
      isReadOnly: true,
      cssClass: 'WijmoFind',
      cellTemplate: (item) => {
        if (item.item.horExplCrgeUserId === undefined || item.item.horExplCrgeUserId === null) {
          return `<span>${item.text}</span><button id="horExplCrgeUserId" />`;
        } else {
          return `<span>${item.item.horExplCrgeUserNm}</span><button id="horExplCrgeUserId" />`;
        }
      },
    },
    {
      binding: 'horExplCrgeUserNm',
      header: String(t('mp.label.수평전개 담당자', '수평전개 담당자')),
      visible: false,
    },
    {
      binding: 'rmk',
      visible: false,
    },
    {
      binding: 'mpItemStatCd',
      visible: false,
    },
  ];

  const mpPlntOnInitialized = (grid) => {
    setMpPlntFlexRef(grid);
    gridRef.current = grid;
    grid.itemFormatter = (panel, row, col, cell) => {
      if (CellType.Cell === panel.cellType) {
        const binding = panel.columns[col].binding;
        // 수평전개담당자 editable css 적용
        if ('horExplCrgeUserId' === binding) {
          cell.ariaReadOnly = false;
        }
      }
    };

    grid.hostElement.addEventListener('click', (e) => {
      const len = grid.rows.length;
      if (len == 0) return;
      const ht = grid.hitTest(e);
      grid.startEditing(true);

      if (ht.row < 0 || ht.col < 0) return;
      if (ht.panel.cellType != 1) return; // 선택된 영역이 셀이 아니면 나가리.

      const item = grid.rows[ht.row].dataItem; // 선택한 row의 정보

      if (e.target instanceof HTMLButtonElement) {
        const col = grid.columns[ht.col]; // 선택한 컬럼의 정보
        const item = grid.rows[ht.row].dataItem; // 선택한 row의 정
        setMpPlntFlexItem(item);
        const userIds = item[e.target.id];
        switch (e.target.id) {
          case 'horExplCrgeUserId':
            setOpenUserMultiModal(true);
            setUserIds(item.horExplCrgeUserId);
            setRmkContext(item.rmk);
            setRequiredRmk(!!item.mpItemStatCd);
            break;
        }
      }
    });
  };

  const callbackUserData = (callbackData) => {
    if (_.isEmpty(callbackData)) {
      openMessageBar({
        type: 'warning',
        content: t('com.msg.선택된 사용자가 없습니다.', '선택된 사용자가 없습니다.'),
      });
      mpPlntFlexItem['horExplCrgeUserId'] = '';
      mpPlntFlexItem['horExplCrgeUserNm'] = '';
      return false;
    }
    mpPlntFlexItem['horExplCrgeUserId'] = callbackData.userId;
    mpPlntFlexItem['horExplCrgeUserNm'] = callbackData.empNm;
    mpPlntFlexItem['rmk'] = callbackData.rmk;
    const row = gridRef.current.rows;
    for (let i = 0; i < row.length; i++) {
      if (row[i].dataItem.prdnPlntCd === mpPlntFlexItem.prdnPlntCd) {
        row[i].isSelected = true;
        gridRef.current.refresh();
      }
    }
  };

  const getSelectedRow = (conditionRowData) => {
    const row = gridRef.current.rows;
    for (let i = 0; i < row.length; i++) {
      for (let j = 0; j < conditionRowData.length; j++) {
        if (row[i].dataItem.prdnPlntCd === conditionRowData[j].prdnPlntCd) {
          row[i].isSelected = true;
          gridRef.current.refresh();
        }
      }
    }
  };

  const handleSearchParam = (name: string, value: string) => {
    setSearchParam({
      ...searchParam,
      [name]: value,
    });
  };

  const handleConfirm = () => {
    const selectedRowNodes = gridRef.current.selectedRows;
    const isSelected = selectedRowNodes.filter((item) => item.isSelected);
    const selectedData = isSelected.map(({ dataItem }) => dataItem);

    const emptyUser = selectedData
      .map((data) => {
        if (_.isEmpty(data.horExplCrgeUserId) || _.isEmpty(data.horExplCrgeUserNm)) {
          return `${t(
            'mp.msg.수평전개 담당자를 입력해 주세요.',
            '수평전개 담당자를 입력해 주세요.'
          )}`;
        }
      })
      .filter((element) => element !== undefined);

    if (emptyUser.length) {
      const content = emptyUser[0]?.toString();
      openMessageBar({ type: 'error', content: content || '' });
      return false;
    }

    if (_.isEmpty(selectedData)) {
      openCommonModal({
        modalType: 'confirm',
        content: t(
          `com.msg.수정된 항목이 없습니다. 진행 시 등록된 Factory가 삭제됩니다. 진행하시겠습니까?`,
          `수정된 항목이 없습니다. 진행 시 등록된 Factory가 삭제됩니다. 진행하시겠습니까?`
        ),
        yesCallback: () => {
          onCallbackData(selectedData);
          setSearchState(false);
          handleClose();
          return true;
        },
        noCallback: () => {
          return false;
        },
      });
    } else {
      onCallbackData(selectedData);
      setSearchState(false);
      handleClose();
    }
  };

  const dialogButtons = [
    <Button
      key={'confirm'}
      css={IconButton.button}
      className="confirm"
      onClick={handleConfirm}
      disableRipple
    >
      {t('com.button.선택', '선택')}
    </Button>,
  ];

  const handleClose = () => {
    setSearchState(false);
    close();
  };

  return (
    <>
      <CustomDialog
        open={open}
        title={t('mp.label.수평전개 대상 Factory', '수평전개 대상 Factory')}
        size="lg"
        onClose={handleClose}
        onCancel={handleClose}
        buttons={dialogButtons}
      >
        <SearchBox>
          <SearchBoxRow>
            <InputBox>
              <SearchRows>
                <SearchCols>
                  <label>{t('com.label.법인', '법인')}</label>
                  <div className="txt">
                    <span>{condition?.copCd}</span>
                  </div>
                </SearchCols>
                <SearchCols>
                  <label>{t('com.label.FACTORY ID', 'FACTORY ID')}</label>
                  <CustomInputWithSearch
                    type="text"
                    name="prdnPlntCd"
                    placeholder={String(
                      t('mp.label.Factory ID를 입력해 주세요.', 'Factory ID를 입력해 주세요.')
                    )}
                    value={searchParam?.prdnPlntCd}
                    onChange={(e) => handleSearchParam(e.target.name, e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        getFactorysList();
                      }
                    }}
                  />
                </SearchCols>
                <SearchCols>
                  <label>{t('com.label.FACTORY 명', 'FACTORY 명')}</label>
                  <CustomInputWithSearch
                    type="text"
                    name="prdnPlntNm"
                    placeholder={String(
                      t('mp.label.Factory 명을 입력해 주세요.', 'Factory 명을 입력해 주세요.')
                    )}
                    value={searchParam?.prdnPlntNm}
                    onChange={(e) => handleSearchParam(e.target.name, e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        getFactorysList();
                      }
                    }}
                  />
                </SearchCols>
              </SearchRows>
            </InputBox>
            <SearchBoxButtons setReset={setSearchParam} onSearch={getFactorysList} />
          </SearchBoxRow>
        </SearchBox>
        <SubTitleLayout>
          <SubTitleGroup>
            <h3>{t('mp.label.공장 List', '공장 List')}</h3>
            <span className="total">
              {t('com.label.총', '총')}
              <span>{(mpPlntRowData || []).length.toLocaleString()}</span>
              {t('com.label.건', '건')}
            </span>
          </SubTitleGroup>
        </SubTitleLayout>
        <CustomGrid
          layoutDefinition={mpPlntLayoutDefinition}
          rowData={mpPlntRowData}
          allowPinning={false}
          height={450}
          align={'center'}
          initialized={mpPlntOnInitialized}
        />
      </CustomDialog>
      {isOpenUserMultiModal && (
        <UserMultiModal
          open={isOpenUserMultiModal}
          close={() => setOpenUserMultiModal(false)}
          title={t('com.label.담당자 조회', '담당자 조회')}
          isOnlyUsed={true}
          // defaultUserId={userIds}
          condition={{
            userId: userIds || '',
            empNm: '',
            rmk: rmkContext || '',
          }}
          singleSelect={true}
          rmk={true}
          isRequireRmk={isRequiredRmk}
          onCallback={(callbackData: any) => {
            callbackUserData(callbackData);
          }}
        />
      )}
    </>
  );
};
